import PropTypes from 'prop-types';
import React from 'react';
import { Tooltip } from 'components/FormFields';
import { StyledButton } from './StyledComponents';

/**
 * ButtonV2 - A unified button component supporting multiple styles
 *
 * @param {Object} props
 * @param {string} [props.variant='default'] - Button style variant: 'default', 'pill', or 'icon'
 * @param {boolean} [props.active=false] - Whether the button is in active/selected state
 * @param {boolean} [props.elevated=false] - Whether to use elevated style (enhanced shadow, darker text in light theme)
 * @param {boolean} [props.primary=false] - Whether to use primary style (filled orange background)
 * @param {boolean} [props.danger=false] - Whether to use danger/destructive style (red coloring)
 * @param {React.ReactNode} [props.icon] - Optional icon element to display
 * @param {React.ReactNode} props.children - Button text/content
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {string} [props.title] - Tooltip text
 * @param {string} [props.type='button'] - Button type attribute
 *
 * @example
 * // Default action button with icon
 * <ButtonV2 icon={<SendIcon />} onClick={handleSend}>
 *   Send Campaign
 * </ButtonV2>
 *
 * @example
 * // Primary button (filled orange)
 * <ButtonV2 primary onClick={handleExport}>
 *   Export PDF Report
 * </ButtonV2>
 *
 * @example
 * // Pill button for filters
 * <ButtonV2 variant="pill" active={isActive} onClick={handleFilter}>
 *   All Customers
 * </ButtonV2>
 *
 * @example
 * // Icon-only button with tooltip
 * <ButtonV2 variant="icon" icon={<FilterIcon />} onClick={handleFilter} title="Filter" />
 *
 * @example
 * // Elevated button (for QuickActions)
 * <ButtonV2 elevated icon={<PrintIcon />} onClick={handlePrint}>
 *   Print Tickets
 * </ButtonV2>
 *
 * @example
 * // Danger button for destructive actions
 * <ButtonV2 danger icon={<TrashIcon />} onClick={handleDelete}>
 *   Delete
 * </ButtonV2>
 */
const ButtonV2 = ({
  variant = 'default',
  active = false,
  elevated = false,
  primary = false,
  danger = false,
  icon,
  children,
  onClick,
  disabled = false,
  title,
  type = 'button',
  ...rest
}) => {
  const button = (
    <StyledButton
      variant={variant}
      active={active}
      elevated={elevated}
      primary={primary}
      danger={danger}
      onClick={onClick}
      disabled={disabled}
      type={type}
      {...rest}
    >
      {icon}
      {children}
    </StyledButton>
  );

  // Wrap with Tooltip if title is provided
  if (title) {
    return <Tooltip text={title}>{button}</Tooltip>;
  }

  return button;
};

ButtonV2.propTypes = {
  variant: PropTypes.oneOf(['default', 'pill', 'icon']),
  active: PropTypes.bool,
  elevated: PropTypes.bool,
  primary: PropTypes.bool,
  danger: PropTypes.bool,
  icon: PropTypes.node,
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.string,
};

export default ButtonV2;
